/**
 * Editor nodes composable
 * Handles node insertion for contenteditable editor (images, tags, colors)
 */

import { i18n } from '@/i18n';
import {
  createMaterialElement,
  updateMaterialNode,
  updateMaterialTagById,
  removeMaterialNodeById,
  createColorTagElement,
  updateColorTagNode,
  removeColorTagNodeById,
  normalizeColorToHex,
  isValidColor,
} from '@/components/create/material/material-utils';

/**
 * @param {Object} options
 * @param {import('vue').Ref<HTMLElement>} options.editorRef - Editor element ref
 * @param {Object} options.chatState - Chat state object
 * @param {Function} options.syncContent - Sync content callback
 * @param {Function} options.makeDraggable - Make element draggable function
 * @param {Function} options.addAssetToChatState - Add asset to chatState function
 * @param {Function} options.removeAssetFromChatState - Remove asset from chatState function
 * @param {Function} options.canAddMore - Check if can add more items function
 * @param {Function} options.toast - Toast notification function
 * @param {Object} options.limits - Limits for materials { MAX_IMAGES, MAX_TAGS, MAX_COLORS }
 * @param {Function} options.onColorClick - Callback when color tag is clicked (colorData, element, event) => void
 * @param {Function} options.onMaterialClick - Callback when material tag is clicked (materialData, element, event) => void
 * @returns {Object} Node insertion handlers
 */
export function useEditorNodes(options = {}) {
  const {
    editorRef,
    chatState,
    syncContent,
    makeDraggable,
    removeAssetFromChatState,
    canAddMore,
    toast,
    limits = {},
    onColorClick,
    onMaterialClick,
  } = options;

  const { MAX_IMAGES = 20, MAX_TAGS = 10, MAX_COLORS = 10 } = limits;

  /**
   * Insert text at cursor position
   * @param {string} text
   */
  const insertTextAtCursor = (text) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    // Move cursor after inserted text
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  /**
   * Create image element using material-utils
   * @param {Object} asset
   * @returns {HTMLElement}
   */
  const createImageElement = (asset) => {
    return createMaterialElement(asset, {
      onRemove: (assetId) => {
        removeAssetFromChatState?.(assetId);
        syncContent?.({ immediate: true });
      },
      onTagClick: onMaterialClick,
      onSync: () => syncContent?.({ immediate: true }),
      makeDraggable,
    });
  };

  /**
   * Insert image node (receives asset object)
   * @param {Object} asset
   * @returns {boolean} Whether the image was inserted successfully
   */
  const insertImageNode = (asset) => {
    if (!editorRef?.value) return false;

    // Check image count limit
    if (canAddMore && !canAddMore('image')) {
      toast?.({
        title: i18n.get('create.maxImagesReached'),
        description: i18n.get('create.maxImagesReachedDesc', { max: MAX_IMAGES }),
        variant: 'destructive',
      });
      return false;
    }

    // Ensure editor has focus
    editorRef.value.focus();

    const selection = window.getSelection();
    let range;

    // If no valid selection or selection not in editor, create new selection
    if (!selection.rangeCount || !editorRef.value.contains(selection.anchorNode)) {
      range = document.createRange();
      // Put cursor at editor end
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch {
        // If adding selection fails, append to editor end
        const container = createImageElement(asset);
        editorRef.value.appendChild(container);
        editorRef.value.appendChild(document.createTextNode('\u00A0'));
        syncContent?.({ immediate: true });
        return true;
      }
    }

    range = selection.getRangeAt(0);

    // Use createMaterialElement to create complete image node (supports usage setting)
    const container = createImageElement(asset);

    range.insertNode(container);

    // Add space after image
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(container);
    range.insertNode(space);

    // Move cursor after space
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      // Ignore cursor set failure
    }

    syncContent?.({ immediate: true });
    return true;
  };

  /**
   * Update image node (for replacing temp node with real asset)
   * @param {string} tempId
   * @param {Object} asset
   */
  const updateImageNode = (tempId, asset) => {
    updateMaterialNode(editorRef?.value, tempId, asset, () => syncContent?.({ immediate: true }));
  };

  /**
   * Remove image node by ID
   * @param {string} assetId
   */
  const removeImageNodeById = (assetId) => {
    removeMaterialNodeById(editorRef?.value, assetId, () => syncContent?.({ immediate: true }));
  };

  /**
   * Get tag class name
   * @param {string} color
   * @returns {string}
   */
  const getTagClassName = (color) => {
    // Notion/Linear style tag colors - softer tones
    const colorMap = {
      blue: 'bg-blue-50/80 text-blue-700 ring-blue-200/50',
      purple: 'bg-violet-50/80 text-violet-700 ring-violet-200/50',
      green: 'bg-emerald-50/80 text-emerald-700 ring-emerald-200/50',
      orange: 'bg-amber-50/80 text-amber-700 ring-amber-200/50',
      pink: 'bg-pink-50/80 text-pink-700 ring-pink-200/50',
      red: 'bg-rose-50/80 text-rose-700 ring-rose-200/50',
      indigo: 'bg-indigo-50/80 text-indigo-700 ring-indigo-200/50',
      teal: 'bg-teal-50/80 text-teal-700 ring-teal-200/50',
    };
    return `inline-flex items-center gap-1 mx-0.5 px-2 py-0.5 rounded-md text-[13px] font-medium ring-1 ring-inset select-none align-middle cursor-grab draggable-item ${
      colorMap[color] || colorMap.blue
    }`;
  };

  /**
   * Insert tag
   * @param {Object} tag - Tag object { id, label, color }
   * @param {Object} options
   * @param {Function} options.hideMenu - Function to hide tag menu
   */
  const insertTag = (tag, insertOptions = {}) => {
    const { hideMenu } = insertOptions;

    // Check tag count limit
    if (canAddMore && !canAddMore('tag')) {
      toast?.({
        title: i18n.get('create.maxTagsReached'),
        description: i18n.get('create.maxTagsReachedDesc', { max: MAX_TAGS }),
        variant: 'destructive',
      });
      hideMenu?.();
      return;
    }

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;

    if (textNode.nodeType === Node.TEXT_NODE) {
      const textBeforeCursor = textNode.textContent.slice(0, range.startOffset);
      const match = textBeforeCursor.match(/#([^\s#]*)$/);

      if (match) {
        // Delete #query part
        range.setStart(textNode, match.index);
        range.deleteContents();

        // Create tag element
        const tagEl = document.createElement('span');
        tagEl.contentEditable = 'false';
        tagEl.setAttribute('data-type', 'tag');
        tagEl.setAttribute('data-tag-id', tag.id);
        tagEl.setAttribute('data-label', tag.label);
        tagEl.setAttribute('data-value', '');
        tagEl.className = getTagClassName(tag.color);

        // Set draggable
        makeDraggable?.(tagEl);

        // Tag content - concise style
        tagEl.innerHTML = `<span class="pointer-events-none">#${tag.label}</span>`;

        range.insertNode(tagEl);

        // Add space after tag
        const space = document.createTextNode('\u00A0');
        range.setStartAfter(tagEl);
        range.insertNode(space);

        // Move cursor
        range.setStartAfter(space);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        hideMenu?.();
        syncContent?.({ immediate: true });
      }
    }
  };

  /**
   * Insert color Tag node (internal use, for inserting when parsing content)
   * @param {Object} colorData
   */
  const insertColorTagNode = (colorData) => {
    if (!editorRef?.value) return;

    const selection = window.getSelection();
    let range;

    if (!selection.rangeCount || !editorRef.value.contains(selection.anchorNode)) {
      range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch {
        // If adding selection fails, append to editor end
        const colorTag = createColorTagElement(colorData, { makeDraggable, onClick: onColorClick });
        editorRef.value.appendChild(colorTag);
        editorRef.value.appendChild(document.createTextNode('\u00A0'));
        return;
      }
    }

    range = selection.getRangeAt(0);

    const colorTag = createColorTagElement(colorData, { makeDraggable, onClick: onColorClick });

    range.insertNode(colorTag);

    // Add space after Tag
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(colorTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      // Ignore cursor set failure
    }
  };

  /**
   * Insert color Tag (can be called via method)
   * @param {string} colorValue
   */
  const insertColorTag = (colorValue) => {
    if (!editorRef?.value || !isValidColor(colorValue)) return;

    // Check color tag count limit
    if (canAddMore && !canAddMore('color')) {
      toast?.({
        title: i18n.get('create.maxColorsReached'),
        description: i18n.get('create.maxColorsReachedDesc', { max: MAX_COLORS }),
        variant: 'destructive',
      });
      return;
    }

    // Ensure editor has focus
    editorRef.value.focus();

    const selection = window.getSelection();
    let range;

    // If no valid selection or selection not in editor, create new selection
    if (!selection.rangeCount || !editorRef.value.contains(selection.anchorNode)) {
      range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch {
        // If adding selection fails, append to editor end
        const colorId = `color_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const normalized = normalizeColorToHex(colorValue);
        const colorTag = createColorTagElement(
          { id: colorId, value: colorValue, displayValue: normalized },
          { makeDraggable, onClick: onColorClick }
        );
        editorRef.value.appendChild(colorTag);
        editorRef.value.appendChild(document.createTextNode('\u00A0'));
        syncContent?.({ immediate: true });
        return;
      }
    }

    range = selection.getRangeAt(0);

    // Create color Tag
    const colorId = `color_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const normalized = normalizeColorToHex(colorValue);
    const colorTag = createColorTagElement(
      { id: colorId, value: colorValue, displayValue: normalized },
      { makeDraggable, onClick: onColorClick }
    );

    range.insertNode(colorTag);

    // Add space after Tag
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(colorTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      // Ignore cursor set failure
    }

    syncContent?.({ immediate: true });
  };

  /**
   * Insert color Tag from input (auto-called when HEX color followed by space is detected)
   * @param {Text} textNode
   * @param {Range} range
   * @param {RegExpMatchArray} match
   * @param {Function} hideMenu - Function to hide tag menu
   */
  const insertColorTagFromInput = (textNode, range, match, hideMenu) => {
    // Check color tag count limit
    if (canAddMore && !canAddMore('color')) {
      toast?.({
        title: i18n.get('create.maxColorsReached'),
        description: i18n.get('create.maxColorsReachedDesc', { max: MAX_COLORS }),
        variant: 'destructive',
      });
      syncContent?.();
      return;
    }

    const colorValue = `#${match[1]}`; // Full HEX color value
    const fullMatch = match[0]; // #RRGGBB + space
    const matchIndex = match.index;

    // Delete #RRGGBB + space
    range.setStart(textNode, matchIndex);
    range.setEnd(textNode, matchIndex + fullMatch.length);
    range.deleteContents();

    // Create color Tag
    const colorId = `color_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const normalized = normalizeColorToHex(colorValue);

    const colorTag = createColorTagElement(
      { id: colorId, value: colorValue, displayValue: normalized },
      { makeDraggable, onClick: onColorClick }
    );

    // Insert color Tag
    range.insertNode(colorTag);

    // Add space after Tag
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(colorTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    hideMenu?.();
    syncContent?.({ immediate: true });
  };

  /**
   * Update material tag (update both editor node and chatState)
   * @param {string} assetId
   * @param {string} tag
   */
  const updateMaterialTag = (assetId, tag) => {
    // Update node in editor
    updateMaterialTagById(editorRef?.value, assetId, tag, syncContent);
    // Update tag in chatState.assets
    if (chatState?.assets) {
      const asset = chatState.assets.find((a) => a.id === assetId);
      if (asset) {
        asset.tag = tag;
      }
    }
  };

  /**
   * Update color tag value
   * @param {string} colorId - Color tag ID
   * @param {string} newValue - New color value
   */
  const updateColorTag = (colorId, newValue) => {
    if (!editorRef?.value) return;
    const normalized = normalizeColorToHex(newValue);
    updateColorTagNode(
      editorRef.value,
      colorId,
      { value: newValue, displayValue: normalized },
      () => {
        syncContent?.({ immediate: true });
      }
    );
  };

  /**
   * Remove color tag by ID
   * @param {string} colorId - Color tag ID
   */
  const removeColorTag = (colorId) => {
    if (!editorRef?.value) return;
    removeColorTagNodeById(editorRef.value, colorId, () => {
      syncContent?.({ immediate: true });
    });
  };

  return {
    insertTextAtCursor,
    createImageElement,
    insertImageNode,
    updateImageNode,
    removeImageNodeById,
    insertTag,
    insertColorTagNode,
    insertColorTag,
    insertColorTagFromInput,
    updateMaterialTag,
    updateColorTag,
    removeColorTag,
    getTagClassName,
  };
}
